<!-- Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com -->
<!-- See license file. -->
<script setup lang="ts">
import {
  computed,
  ref,
  onMounted,
  onUpdated,
  useAttrs,
  watchEffect,
  watch,
} from "vue";

const props = defineProps<{
  label?: String;
  labelClass?: String;
  footer?: String;
  footerClass?: String;
  modelValue: string | undefined;
  options: Array<{ text: string; value: string | undefined }>;
}>();

const emit = defineEmits(["update:modelValue"]);

const inputRef = ref<HTMLInputElement | null>(null);
const labelRef = ref<HTMLLabelElement | null>(null);
const notchLeadingWidth = ref(9);
const notchMiddleWidth = ref(0);

const inputValue = ref(props.modelValue);
const attrs = useAttrs();

const inputClassName = computed(() => {
  return ["input-input", inputValue.value && "active"];
});

const labelClassName = computed(() => {
  return ["input-label", props.labelClass];
});

const footerClassName = computed(() => {
  return ["input-footer", props.footerClass];
});

watch(inputValue, () => {
  emit("update:modelValue", inputValue.value);
});

const notchRef = ref<HTMLElement | null>(null);
const inputNotchElements = ref<NodeListOf<HTMLInputElement>>();

function recomputeNotchWidth() {
  if (labelRef.value) {
    notchMiddleWidth.value = labelRef.value.clientWidth + 5;
  }
}

onMounted(() => {
  recomputeNotchWidth();

  if (!inputRef.value) {
    return;
  }

  if (props.label && inputRef.value.parentNode) {
    inputNotchElements.value =
      inputRef.value.parentNode.querySelectorAll(".input-notch");
  }
});

onUpdated(() => {
  recomputeNotchWidth();
});

watchEffect(() => {
  inputValue.value = props.modelValue;
});
</script>

<template>
  <select
    :class="inputClassName"
    v-bind="attrs"
    v-model="inputValue"
    ref="inputRef"
  >
    <option disabled value="">Aucune selection</option>
    <option
      v-for="option in props.options"
      :value="option.value"
      :key="option.value || option.text"
    >
      {{ option.text }}
    </option>
  </select>
  <label
    v-if="label"
    ref="labelRef"
    :class="labelClassName"
    :disabled="attrs.disabled"
  >
    {{ label }}
  </label>
  <div class="input-notch" ref="notchRef" :disabled="attrs.disabled">
    <div
      class="input-notch-leading"
      :style="{ width: `${notchLeadingWidth}px` }"
    ></div>
    <div
      class="input-notch-middle"
      :style="{ width: `${notchMiddleWidth}px` }"
    ></div>
    <div class="input-notch-trailing"></div>
  </div>
  <div v-if="footer" :class="footerClassName">{{ footer }}</div>
</template>

<style lang="scss">
// Removing the arrows for numbers

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  appearance: textfield;
  -moz-appearance: textfield;
}

input.input-input,
.input-input {
  display: block;
  width: 100%;

  border: 0;
  border-radius: 3px;

  padding-top: 0.32rem;
  padding-bottom: 0.32rem;
  padding-left: 0.75rem;
  padding-right: 0.75rem;

  margin-top: 0.5em;

  background: var(--color-background-soft-contrast);
  transition: all 0.2s linear;

  border-color: #bdbdbd;

  &:focus {
    ~ {
      .input-notch {
        div {
          border-color: var(--vt-c-purple);
          border-width: 2px;
        }
      }
    }
  }
}

.input-label {
  position: absolute;
  top: -0.52em; // -1 +margin-top -0.2
  left: 0.75rem;

  padding-top: 0.37rem;
  margin-bottom: 0;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;

  transition: all 0.2s ease-out;

  color: var(--vt-c-purple);

  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 16px;
}

.input-footer {
  position: absolute;
  bottom: -0.6em;
  left: 0.7em;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;

  transition: all 0.2s ease-out;

  color: rgba(33, 53, 71, 0.6);

  font-style: normal;
  font-weight: 500;
  font-size: 8px;
  line-height: 10px;
}

.input-notch {
  display: flex;
  position: absolute;
  left: 0;
  bottom: 0;

  width: 100%;
  max-width: 100%;
  height: calc(100% - 0.5em);

  text-align: left;
  pointer-events: none;

  div {
    pointer-events: none;
    border: 1px solid;
    background: rgba(0, 0, 0, 0);
  }

  &[disabled="true"] div {
    border: none;
  }

  .input-notch-leading {
    border-right: none;
    border-radius: 0.25rem 0 0 0.25rem;
  }

  .input-notch-middle {
    border-right: none;
    border-left: none;
    border-top: none;
  }

  .input-notch-trailing {
    flex-grow: 1; // fill space
    border-left: none;
    border-radius: 0 0.25rem 0.25rem 0;
  }
}
</style>
